import { SEARCH_PARAM } from '@/constants/searchParams';
import { SEARCH_TYPE } from '@/constants/searchType';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { PUBLIC_SEARCH_STATE_PARAM } from '@/lib/saleCataloguePaths';

export type SearchType =
  | typeof SEARCH_TYPE.ANIMAL
  | typeof SEARCH_TYPE.MEMBERS
  | typeof SEARCH_TYPE.MATING
  | typeof SEARCH_TYPE.SALE_CATALOGUES
  | typeof SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS
  | typeof SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS
  | typeof SEARCH_TYPE.SALE_CATALOGUES_NOTES
  | typeof SEARCH_TYPE.DOWNLOADS
  | typeof SEARCH_TYPE.ONLINE_REGISTRATIONS;

/**
 * True when the current URL carries public-search state (`?search=`). The URL
 * is the source of truth for the active query/filters/sort (kept in sync by
 * `usePublicSearchUrlSync`); the persisted sessionStorage stores defer to it.
 * Returns false during SSR (no `window`).
 */
export function hasPublicSearchUrlState(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = new URLSearchParams(window.location.search).get(
    PUBLIC_SEARCH_STATE_PARAM,
  );
  return !!raw;
}

const EBV_PERCENTAGES_PATH = 'ebv-percentages';
const ANIMALS_SUBPATHS = new Set([ROUTE_SEGMENT.MATING, EBV_PERCENTAGES_PATH]);

/**
 * Society-scoped public database UI lives under /{org}/animals, /members, /sales (list + /sales/{id}), /external (no /search).
 */
const SOCIETY_PUBLIC_PREFIX = new RegExp(
  `^/[^/]+/(${[
    ROUTE_SEGMENT.ANIMALS,
    ROUTE_SEGMENT.MEMBERS,
    ROUTE_SEGMENT.SALES,
    ROUTE_SEGMENT.EXTERNAL,
  ].join('|')})(/|$)`,
);

/**
 * Derive internal SearchType from society public pathname.
 * Returns null if pathname is not under a known society public route.
 */
export function parsePathnameToSearchType(
  pathname: string | null,
): SearchType | null {
  if (!pathname || !SOCIETY_PUBLIC_PREFIX.test(pathname)) return null;

  const tailMatch = pathname.match(/^\/[^/]+\/(.+)$/);
  const segments = (tailMatch?.[1] ?? '').split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const [a, b, c] = segments;

  if (a === ROUTE_SEGMENT.ANIMALS) {
    if (segments.length === 1) return SEARCH_TYPE.ANIMAL;
    if (segments.length === 2 && b === ROUTE_SEGMENT.SALES)
      return SEARCH_TYPE.SALE_CATALOGUES;
    if (segments.length === 2 && b === ROUTE_SEGMENT.MATING)
      return SEARCH_TYPE.MATING;
    if (segments.length === 2 && b === EBV_PERCENTAGES_PATH)
      return SEARCH_TYPE.ANIMAL;
    if (segments.length === 2 && b && !ANIMALS_SUBPATHS.has(b))
      return SEARCH_TYPE.ANIMAL;
    return null;
  }

  if (a === ROUTE_SEGMENT.MEMBERS) return SEARCH_TYPE.MEMBERS;

  if (a === ROUTE_SEGMENT.SALES) {
    if (segments.length === 1) return SEARCH_TYPE.SALE_CATALOGUES;
    if (segments.length === 2 && b)
      return SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS;
    if (segments.length === 3 && b && c === ROUTE_SEGMENT.NOTES)
      return SEARCH_TYPE.SALE_CATALOGUES_NOTES;
    return null;
  }

  if (a === ROUTE_SEGMENT.EXTERNAL) {
    if (b === ROUTE_SEGMENT.REGISTRATIONS)
      return SEARCH_TYPE.ONLINE_REGISTRATIONS;
    if (b === ROUTE_SEGMENT.DOWNLOADS) return SEARCH_TYPE.DOWNLOADS;
  }

  return null;
}

/**
 * Build pathname + query for a society public URL. Preserves unrelated query keys (e.g. shared, filters).
 * Removes searchType and saleId from query when encoded in the path.
 */
export function pathForSearchType(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): { pathname: string; query: URLSearchParams } {
  const q = new URLSearchParams(existingParams?.toString() ?? '');
  q.delete(SEARCH_PARAM.SEARCH_TYPE);

  const base = `/${orgCode}`;

  switch (searchType) {
    case SEARCH_TYPE.ANIMAL:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/${ROUTE_SEGMENT.ANIMALS}`, query: q };
    case SEARCH_TYPE.MEMBERS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/${ROUTE_SEGMENT.MEMBERS}`, query: q };
    case SEARCH_TYPE.SALE_CATALOGUES:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/${ROUTE_SEGMENT.SALES}`, query: q };
    case SEARCH_TYPE.MATING:
      q.delete(SEARCH_PARAM.SALE_ID);
      return {
        pathname: `${base}/${ROUTE_SEGMENT.ANIMALS}/${ROUTE_SEGMENT.MATING}`,
        query: q,
      };
    case SEARCH_TYPE.DOWNLOADS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return {
        pathname: `${base}/${ROUTE_SEGMENT.EXTERNAL}/${ROUTE_SEGMENT.DOWNLOADS}`,
        query: q,
      };
    case SEARCH_TYPE.ONLINE_REGISTRATIONS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return {
        pathname: `${base}/${ROUTE_SEGMENT.EXTERNAL}/${ROUTE_SEGMENT.REGISTRATIONS}`,
        query: q,
      };
    case SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS:
    case SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS: {
      const saleId =
        existingParams?.get(SEARCH_PARAM.SALE_ID) ??
        q.get(SEARCH_PARAM.SALE_ID) ??
        undefined;
      q.delete(SEARCH_PARAM.SALE_ID);
      // Lot listing search is independent of the sale catalogues list search.
      q.delete(PUBLIC_SEARCH_STATE_PARAM);
      q.delete(SEARCH_PARAM.LOT_NO);
      if (!saleId) {
        return { pathname: `${base}/${ROUTE_SEGMENT.SALES}`, query: q };
      }
      return {
        pathname: `${base}/${ROUTE_SEGMENT.SALES}/${saleId}`,
        query: q,
      };
    }
    case SEARCH_TYPE.SALE_CATALOGUES_NOTES: {
      const saleId =
        existingParams?.get(SEARCH_PARAM.SALE_ID) ??
        q.get(SEARCH_PARAM.SALE_ID) ??
        undefined;
      q.delete(SEARCH_PARAM.SALE_ID);
      if (!saleId) {
        return { pathname: `${base}/${ROUTE_SEGMENT.SALES}`, query: q };
      }
      return {
        pathname: `${base}/${ROUTE_SEGMENT.SALES}/${saleId}/${ROUTE_SEGMENT.NOTES}`,
        query: q,
      };
    }
    default:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/${ROUTE_SEGMENT.ANIMALS}`, query: q };
  }
}

export function buildSocietySearchHref(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): string {
  const { pathname, query } = pathForSearchType(
    orgCode,
    searchType,
    existingParams,
  );
  const qs = query.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
